import { InputText } from "primereact/inputtext";
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { DataTable, DataTableSelectionSingleChangeEvent } from "primereact/datatable";
import { Column, ColumnEditorOptions } from "primereact/column";
import { useEffect, useRef, useState } from "react";
import { BomCostingService } from "../../service/BomCostingService";
import WorkCenter from "../../models/WorkCenter";
import { Panel } from "primereact/panel";
import { Controller, useForm } from "react-hook-form";
import { Button } from "primereact/button";
import { classNames } from 'primereact/utils';
import { Toast } from "primereact/toast";
import { Dialog } from "primereact/dialog";
import React from "react";
import { Link } from "react-router-dom";

export default function WorkCenterView() {
    let empty: WorkCenter = {
        id: 0,
        code: '',
        description: '',
        isUsed: false
    };
    const defaultValues: Record<string, any> = {
        id: 0,
        code: '',
        description: ''
    };

    const [listValues, setListValues] = useState<WorkCenter[]>([]);
    const [singleValue, setSingleValue] = useState<WorkCenter>(empty);
    const [loading, setLoading] = useState(true);
    const [globalFilter, setGlobalFilter] = useState('');
    const dt = useRef<any>(null);
    const isNew = useRef(false);
    const form = useForm({ defaultValues });
    const formEdit = useForm({ mode: "onChange" });
    const [editingRows, setEditingRows] = useState({});
    const errors = form.formState.errors;
    const toast = useRef<any>(null);
    const [deleteDialog, setDeleteDialog] = useState(false);

    useEffect(() => {
        BomCostingService.getAllWorkCenters().then(data => {
            setListValues(data);
            setLoading(false);
        });
    }, []);

    const header = (
        <div className="flex flex-column md:flex-row md:justify-content-end md:align-items-center">
            <IconField iconPosition="left">
                <InputIcon className="pi pi-search" />
                <InputText className="p-inputtext-sm" type="search" autoComplete="off" value={globalFilter} onInput={(e) => setGlobalFilter((e.target as HTMLInputElement).value)} placeholder="Search..." />
            </IconField>
        </div>
    );

    const CodeBodyTemplate = (rowData: WorkCenter) => {
        return rowData.code;
    }

    const NameBodyTemplate = (rowData: WorkCenter) => {
        return rowData.description;
    }

    const onSubmit = (data: any) => {
        isNew.current = true;
        let _value = { ...singleValue };
        _value.code = data.code;
        _value.description = data.description;
        save(_value);
    };

    const getErrorMessage = (name: string) => {
        return errors[name] ? <small className="p-error">{(errors[name] as any).message}</small> : <small className="p-error">&nbsp;</small>;
    };

    const clearNew = () => {
        form.reset();
    }

    const save = (value: WorkCenter) => {
        let _listValues = [...listValues];

        if (isNew.current) {
            BomCostingService.addWorkCenter(value).then((data) => {
                if (data) {
                    _listValues.push(data);
                    setListValues(_listValues);
                    toast.current.show({ severity: 'success', summary: 'Successful', detail: 'Work Center Created', life: 3000 });
                    setSingleValue(empty);
                    dt.current.props.value = _listValues;
                    form.reset();
                } else {
                    toast.current.show({ severity: 'error', summary: 'Failed', detail: 'Failed to Create Work Center', life: 3000 });
                }
            });
        } else {
            BomCostingService.updateWorkCenter(value).then((data) => {
                if (data) {
                    let index = _listValues.findIndex(v => v.id === value.id);
                    _listValues[index] = value;
                    setListValues(_listValues);
                    toast.current.show({ severity: 'success', summary: 'Successful', detail: 'Work Center Updated', life: 3000 });
                    setSingleValue(empty);
                } else {
                    toast.current.show({ severity: 'error', summary: 'Failed', detail: 'Failed to Update Work Center', life: 3000 });
                }
            });
        }
    }

    const setActiveRowIndex = (rowData: DataTableSelectionSingleChangeEvent<WorkCenter[]>) => {
        let selectedIndex = listValues.findIndex(v => v.id === (rowData.value as WorkCenter).id);
        let _editingRows = { ...editingRows, ...{ [`${listValues[selectedIndex].id}`]: true } };

        if (Object.keys(_editingRows).length === 1) {
            setEditingRows(_editingRows);
        }
    }

    const cellEditor = (options: ColumnEditorOptions) => {
        switch (options.field) {
            case 'code':
                return codeEditor(options);

            case 'description':
                return descriptionEditor(options);
        }
    }

    const codeEditor = (options: any) => {
        return (
            <Controller
                name="codeEdit"
                control={formEdit.control}
                defaultValue={options.value}
                rules={{
                    required: 'Code is required.',
                    validate: {
                        unique: v => listValues.filter(val => val.code === v).length === 0 || 'Code must be unique.'
                    }
                }}
                render={({ field, fieldState }) => {
                    singleValue.code = field.value;

                    return (
                        <>
                            <InputText ref={field.ref} value={fieldState.isDirty ? field.value : options.value} maxLength={50} tooltip={fieldState.error?.message} tooltipOptions={{ position: 'bottom' }} className={classNames({ 'p-invalid border-left-10': fieldState.error })} onChange={(e) => { singleValue.code = e.target.value; field.onChange(e.target.value); }} placeholder="Code" />
                        </>
                    )
                }
                }
            />
        );
    }

    const descriptionEditor = (options: any) => {
        return (
            <Controller
                name="descriptionEdit"
                control={formEdit.control}
                defaultValue={options.value}
                rules={{
                    required: 'A name is required.'
                }}
                render={({ field, fieldState }) => {
                    singleValue.description = field.value;

                    return (
                        <>
                            <InputText ref={field.ref} value={fieldState.isDirty ? field.value : options.value} maxLength={50} tooltip={fieldState.error?.message} tooltipOptions={{ position: 'bottom' }} className={classNames({ 'p-invalid border-left-10': fieldState.error })} onChange={(e) => { singleValue.description = e.target.value; field.onChange(e.target.value); }} placeholder="Description" />
                        </>
                    )
                }
                }
            />
        );
    }

    const onRowEditChange = (e: any) => {
        setEditingRows(e.data);
    }

    const onRowEditComplete = (e: any) => {
        let _workCenters = [...listValues];
        let { newData, index } = e;

        _workCenters[index] = newData;

        setListValues(_workCenters);
    }

    const confirmDelete = (value: WorkCenter) => {
        setSingleValue(value);
        setDeleteDialog(true);
    }

    const rowEditorTemplate = (rowData: WorkCenter, props: any) => {
        const rowEditor = props.rowEditor;

        if (rowEditor.editing) {
            isNew.current = false;

            return (
                <>
                    <Button icon="p-row-editor-save-icon pi pi-fw pi-save" title="Save" rounded text severity="info" type="submit" onClick={(e) => {
                        if (!formEdit.getFieldState("codeEdit").invalid && !formEdit.getFieldState("descriptionEdit").invalid) {
                            let _singleValue = { ...singleValue };
                            _singleValue.id = rowData.id;
                            _singleValue.code = rowData.code;
                            save(_singleValue);
                            formEdit.reset();
                            rowEditor.onSaveClick(e);
                        }
                    }} />

                    <Button icon="p-row-editor-cancel-icon pi pi-fw pi-undo" title="Undo" rounded text severity="danger" onClick={(e) => {
                        formEdit.clearErrors();
                        formEdit.reset();
                        rowEditor.onCancelClick(e);
                    }} />
                </>
            );
        } else {
            return (
                !rowData.isUsed
                ?
                    <>
                        <Link className="pi pi-fw pi-trash rowDelete" title="Delete" onClick={() => confirmDelete(rowData)} to={''}></Link>
                    </>
                : <></>
            );
        }
    }

    const hideDeleteDialog = () => {
        setDeleteDialog(false);
    };

    const deleteRow = () => {
        let _operations = listValues.filter((val) => val.id !== singleValue.id);
        BomCostingService.deleteWorkCenter(singleValue.id).then((data: boolean) => {
            if (data) {
                setListValues(_operations);
                setDeleteDialog(false);
                setSingleValue(empty);
                toast.current.show({ severity: 'success', summary: 'Successful', detail: 'Deleted', life: 3000 });
            } else {
                toast.current.show({ severity: 'error', summary: 'Failed', detail: 'Failed to remove', life: 3000 });
            }
        });
    };

    const deleteDialogFooter = (
        <React.Fragment>
            <Button label="No" icon="pi pi-times" className="p-button-text" onClick={hideDeleteDialog} />
            <Button label="Yes" icon="pi pi-check" className="p-button-text" onClick={deleteRow} />
        </React.Fragment>
    );

    return (
        <>
            <Toast ref={toast} />

            <Panel header="Add Work Center" className="addPanel mb-2 -ml-2">
                <form onSubmit={form.handleSubmit(onSubmit)} className="p-fluid" autoComplete="off">
                    <div className="formgrid grid">
                        <div className="field col-2">
                            <Controller
                                name="code"
                                control={form.control}
                                rules={{
                                    required: 'Code is required.',
                                    validate: {
                                        unique: v => listValues.filter(val => val.code.toUpperCase() === v.toUpperCase()).length === 0 || 'Code must be unique.'
                                    }
                                }}
                                render={({ field, fieldState }) => (
                                    <>
                                        <label htmlFor={field.name} className="font-bold text-xs">Code</label>
                                        <InputText id={field.name} name={field.name} value={field.value} maxLength={50} className={classNames({ 'p-invalid border-left-10': fieldState.error })} onChange={(e) => field.onChange(e.target.value.toUpperCase())} placeholder="Code" />
                                        {getErrorMessage('code')}
                                    </>
                                )}
                            />
                        </div>
                        <div className="field col-2">
                            <Controller
                                name="description"
                                control={form.control}
                                rules={{
                                    required: 'A name is required.'
                                }}
                                render={({ field, fieldState }) => (
                                    <>
                                        <label htmlFor={field.name} className="font-bold text-xs">Description</label>
                                        <InputText id={field.name} name={field.name} value={field.value} maxLength={50} className={classNames({ 'p-invalid border-left-10': fieldState.error })} onChange={(e) => field.onChange(e.target.value)} placeholder="Description" />
                                        {getErrorMessage('description')}
                                    </>
                                )}
                            />
                        </div>
                        <div className="field field-buttons col-2 pt-4">
                            <Button icon="pi pi-save" title="Save" rounded text severity="info" type="submit" />
                            <Button icon="pi pi-undo" title="Undo" rounded text severity="danger" type="button" onClick={clearNew} />
                        </div>
                    </div>
                </form>
            </Panel>

            <DataTable ref={dt} className="-ml-2" value={listValues} selectionMode="single" onSelectionChange={setActiveRowIndex}
                dataKey="id" size="small" rowHover={true} loading={loading}
                sortField="id"
                scrollable scrollHeight="calc(100vh - 28.2rem)"
                paginator rows={25} rowsPerPageOptions={[5, 10, 25]} alwaysShowPaginator={false}
                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                currentPageReportTemplate="Showing {first} to {last} of {totalRecords} records"
                editingRows={editingRows} editMode="row" onRowEditChange={onRowEditChange} onRowEditComplete={onRowEditComplete}
                globalFilter={globalFilter} emptyMessage="No cost element records found." header={header}>
                <Column field="code" header="Code" sortable body={CodeBodyTemplate} align="center" alignHeader="center" headerStyle={{ width: '10%', minWidth: '10rem' }}></Column>
                <Column field="description" header="Description" sortable body={NameBodyTemplate} editor={(options: ColumnEditorOptions) => cellEditor(options)} align="center" alignHeader="center" headerStyle={{ width: '10%', minWidth: '10rem' }}></Column>
                <Column rowEditor body={rowEditorTemplate} headerStyle={{ width: '10%', minWidth: '8rem' }} bodyStyle={{ textAlign: 'center' }}></Column>
            </DataTable>
            
            <Dialog visible={deleteDialog} style={{ width: '32rem' }} breakpoints={{ '960px': '75vw', '641px': '90vw' }} header="Confirm" modal footer={deleteDialogFooter} onHide={hideDeleteDialog}>
                <div className="confirmation-content">
                    <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: '2rem' }} />
                    {singleValue && (
                        <span>
                            Are you sure you want to delete <b>{singleValue.code} - {singleValue.description}</b>?
                        </span>
                    )}
                </div>
            </Dialog>
        </>
    )
}